import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";

import type { FacilityResponse } from "@bw/shared/types/generated";

import { StatusBadge } from "@/components/StatusBadge";

import type { DetailReadState } from "./OrgCommercialTermsPanel";

export function OrgFacilitiesPanel({
  facilities,
  state,
  onRetry,
}: {
  facilities: FacilityResponse[] | undefined;
  state: DetailReadState;
  onRetry: () => void;
}) {
  const rows = facilities ?? [];

  return (
    <Paper
      component="section"
      aria-labelledby="facilities-heading"
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 } }}
    >
      <Stack spacing={2}>
        <Typography
          id="facilities-heading"
          component="h2"
          variant="h6"
          sx={{ fontWeight: 700 }}
        >
          Facilities
        </Typography>

        {state === "loading" ? (
          <Box
            role="status"
            aria-label="Loading facilities"
            aria-live="polite"
            sx={{ display: "flex", justifyContent: "center", py: 4 }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : state === "error" ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            }
          >
            Facilities could not be loaded. Try again.
          </Alert>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No facilities
          </Typography>
        ) : (
          <Stack
            divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}
          >
            {rows.map((facility) => (
              <Stack
                key={facility.facility_id}
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{
                  alignItems: { sm: "center" },
                  justifyContent: "space-between",
                  py: 1.25,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Link
                    component={RouterLink}
                    to={`/facilities/${facility.facility_id}`}
                    variant="body2"
                    underline="hover"
                    sx={{ fontWeight: 700 }}
                  >
                    {facility.name}
                  </Link>
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{ display: "block" }}
                  >
                    {facility.location}
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ flexWrap: "wrap", justifyContent: { sm: "flex-end" } }}
                >
                  {facility.is_assessment_center && (
                    <StatusBadge status="assessment_center" />
                  )}
                  <Box
                    role="group"
                    aria-label={`Approval status: ${facility.approval_status}`}
                  >
                    <StatusBadge status={facility.approval_status} />
                  </Box>
                  <Box
                    role="group"
                    aria-label={`Operating status: ${facility.status}`}
                  >
                    <StatusBadge status={facility.status} />
                  </Box>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
